import { getSlotToDate, isSlotLessThanThirtyMins } from './slot_helpers';
import { OPLUS_COLOR_PALETTE, SLOT_TYPES } from './models';
import { dateFromSecondsTimestamp, timespansOverlap } from './common';
import { yearMonthDateString } from '../common/common';
import { getCanEdit, openDwsSectionModal } from './summary_dws';
import alertDescriptions, { alertIconMap } from '../model/v1/slotAlerts';

// TODO: Refactor templates to Vue and use these to mount slots to DOM
// TODO: to match prod, I put an extra drag handle in sections overlay. Switch to a drag icon in specific location and remove this.

/** @import { RoleStore } from '../store/v1/roleStore' */

/**
 * @typedef {Object} RawSlot
 * @property {number} ID
 * @property {number} FromUnix - Start time as a Unix timestamp (seconds).
 * @property {number} ToUnix - End time as a Unix timestamp (seconds).
 * @property {number} HideFrom
 * @property {number} HideTo
 * @property {number} Type
 * @property {number} UserID
 * @property {number} DateUnix
 * @property {number} RoleID - ID of the role template this slot/section belongs to.
 * @property {number} SectionParent - ID of the parent slot (0 if not a section).
 * @property {number[]} [Sections] - IDs of child section slots.
 * @property {string} Color
 * @property {string} TypeTextHTML
 * @property {string} TextNoTime
 * @property {boolean} Locked
 * @property {boolean} KOTSynced
 * @property {boolean} IsAuto
 * @property {boolean} LinkPrev
 * @property {boolean} LinkNext
 * @property {boolean} Repeat
 * @property {number} AskStatus
 * @property {number} Alert
 * @property {number} NumComments
 * @property {Object} [RequestedSlot]
 * @property {number} row - Row index assigned during section layout (mutated at render time).
 */

/**
 * Creates the HTML string for a slot element in the daily work schedule table.
 * @param {RawSlot} slot
 * @param {boolean} canEdit - Whether the current user can edit the slot.
 * @param {boolean} isDashBorderRight - Whether to render a dashed right border (e.g. slot spans into the next day column).
 * @param {string} username - The username of the staff member owning the slot.
 * @param {boolean} useShiftBorders - Whether to render shift border styling (dashed outline for unlocked slots).
 * @param {RoleStore} roleStore - Store used to look up role settings for section limit badges.
 * @returns {string} HTML string for the slot element.
 */
export function createSlotDOMElement(slot, canEdit, isDashBorderRight, username, useShiftBorders, roleStore) {
  const slotDate = yearMonthDateString(new Date(slot.DateUnix * 1000), 'Asia/Tokyo');
  return `
    <div
        class="slot slots-cont draggable mb-1 ${!slot.Locked && useShiftBorders ? 'slot-dashed-border' : ''}"
        id="slot-${slot.ID}"
        data-id="${slot.ID}"
        data-user="${slot.UserID}"
        data-date="${slotDate}"
        data-from="${slot.FromUnix}"
        data-to="${slot.ToUnix}"
        data-background-color="${ slot.Color ? `#${slot.Color}` : ''}"
        data-previous="false"
        data-sections="${ slot.Sections ? true : false}"
        data-hide-from="${ slot.HideFrom }"
        data-hide-to="${ slot.HideTo }"
        data-type="${ slot.Type }"
        data-is-clone="false"
        onclick="openSlotModal('${slot.DateUnix}', '${slot.UserID}', '${slot.ID}')"
        style="${createSlotDOMElementStyle(slot, isDashBorderRight)}"
    >

      ${createSlotBadgeElement(slot, canEdit)}

      <div class="draggable-handle drag-handle"><\x2Fdiv>
      <div class="title-outer" style="${slot.RequestedSlot ? 'flex-direction: column;' : ''}">

        ${ slot.RequestedSlot ? `
          <div id="requested-slot_${slot.RequestedSlot.ID}" class="chip requested-slot">
            <span>
              ${ slot.RequestedSlot.Text }
              <i class="zmdi zmdi-info zmdi-hc-lg text-error tooltip tooltip-right" data-tooltip="希望シフト"></i>
            </span>
          </div>` : ''
        }

        <div id="slot-label-${slot.ID}" class="slot-label" style="${slot.RequestedSlot ? 'padding: 0 0.4rem;' : ''}">
         ${createSlotDOMElementChip(slot, useShiftBorders)}
        <\x2Fdiv>
      <\x2Fdiv>
      <div
        class="sections-overlay drag-handle"
        style="${slot.RequestedSlot ? 'align-content: flex-end;' : ''}"
        data-slot-id=${ slot.ID }>
        ${createSections(slot, roleStore)}
      <\x2Fdiv>
    <\x2Fdiv>
  `
}

/**
 * Creates the HTML string for all sections within a slot, including the draw-sections overlay.
 * Sections are sorted and assigned to rows to avoid overlap before rendering.
 * @param {RawSlot} slot - The slot's Sections array may be mutated with sorted IDs.
 * @param {RoleStore} roleStore - Store used to look up role settings for section limit badges.
 * @returns {string} HTML string for the sections overlay content.
 */
export function createSections(slot, roleStore) {
  let html = `
    <div class="draw-sections" data-slot-id="${String(slot.ID)}"><\x2Fdiv>
  `;

  if (slot.Sections) {
    // Sections are not sorted by start time, so we will sort them here for optimum spacing.
    slot.Sections.sort((a, b) => SlotMap[a]?.FromUnix - SlotMap[b]?.FromUnix);

    const sectionsWithRows = getSectionsWithRows(slot.Sections, SlotMap);

    // Sort sections by their rows and by start time within the same row
    sectionsWithRows.sort((a, b) => a.row - b.row || a.FromUnix - b.FromUnix);
    slot.Sections = sectionsWithRows.map(section => section.ID);

    // Compute which section IDs need a limit badge once, so per-section rendering doesn't repeat the sort.
    const limitBadgeSectionIds = computeLimitBadgeSectionIds(slot, roleStore);

    html += slot.Sections.map(sectionId => {
      const section = SlotMap[sectionId];
      if (section && timespansOverlap(dateFromSecondsTimestamp(slot.FromUnix), getSlotToDate(slot), dateFromSecondsTimestamp(section.FromUnix), getSlotToDate(section))) {
        return createSectionDOMElement(section, slot, limitBadgeSectionIds);
      }
    }).join("");
  }

  return html;
}

/**
 * Computes the set of section IDs that should display a limit badge.
 * Groups sections by RoleID, sorts each group by start time, then marks those
 * beyond the role's roleSectionLimitNumber as over-limit.
 * @param {RawSlot} slot
 * @param {RoleStore} roleStore
 * @returns {Set<number>} Set of section IDs that need a badge.
 */
function computeLimitBadgeSectionIds(slot, roleStore) {
  if (!roleStore || !slot.Sections) return new Set();

  /** @type {Record<number, { role: import('../store/v1/roleStore').Role, ids: number[] }>} */
  const byRole = {};

  for (const id of slot.Sections) {
    const section = SlotMap[id];
    if (!section) continue;
    const role = roleStore.byId(String(section.RoleID));
    if (!role || !role.roleSectionLimitEnabled || role.roleSectionLimitNumber <= 0) continue;
    (byRole[section.RoleID] = byRole[section.RoleID] || { role, ids: [] }).ids.push(id);
  }

  const badgeIds = new Set();

  for (const { role, ids: sectionIds } of Object.values(byRole)) {
    sectionIds.sort((a, b) => (SlotMap[a]?.FromUnix || 0) - (SlotMap[b]?.FromUnix || 0));
    for (let i = role.roleSectionLimitNumber; i < sectionIds.length; i++) {
      badgeIds.add(sectionIds[i]);
    }
  }

  return badgeIds;
}

function getSectionsWithRows(sectionIds, SlotMap) {
  const rows = [];
  const sectionsWithRows = [];

  // Assign rows to sections using the greedy approach
  // Each row tracks the end time of the last section in that row
  for (const sectionId of sectionIds) {
    const section = SlotMap[sectionId];
    if (!section) continue;

    const prevRow = section.row !== undefined && rows[section.row] <= section.FromUnix;
    let rowIndex = prevRow ? section.row : rows.findIndex(endTime => endTime <= section.FromUnix);

    if (rowIndex === -1) {
      rowIndex = rows.length;
      rows.push(section.ToUnix); // Add end time to a new row
    } else {
      rows[rowIndex] = section.ToUnix; // Update the end time for the reused row
    }

    section.row = rowIndex;
    sectionsWithRows.push(section);
  }

  return sectionsWithRows;
}

function createSlotDOMElementStyle(slot, isDashBorderRight) {
  const hasSection = slot?.Sections?.length > 0
  return `
    ${ hasSection ? `background: repeating-linear-gradient(135deg, #${slot.Color || "eef0f3"}40 0 15px, #${slot.Color ? `${slot.Color}80` : "eef0f3cc"} 15px 30px);` : "" }
    ${ !hasSection && slot.Color ? `background-color: #${ slot.Color }; color:white;` : ""}
    ${slot.RequestedSlot ? "min-height: 2.4rem;" : ""}
    ${isDashBorderRight ? "border-right-style: dashed; border-right-color: #0004;" : ""}
  `;
}

function createSlotBadgeElement(slot, canEdit) {
  const unreadCommentsCount = slotLogMap[slot.ID]?.length > 9 ? '9+' : slotLogMap[slot.ID]?.length;
  const readCommentsCount = slot.NumComments > 9 ? '9+' : slot.NumComments;
  return `
    <div class="slot-top-right">
      ${ unreadCommentsCount
        ? `<span id="slot-{{$slot.ID}}-comment-badge" class="circle-container comment-number-badge" style="background-color: #53D064;">${ unreadCommentsCount }</span>`
        : (readCommentsCount
            ? `<span id="slot-{{$slot.ID}}-comment-badge" class="circle-container comment-number-badge" style="background-color: #B1BABE;">${ readCommentsCount }</span>`
            : ""
          )
      }
      ${ slot.AskStatus > 0 ? `
        <span class="tooltip tooltip-left text-warning circle-container asked-badge
          ${ slot.AskStatus == 2 ? 'asked-badge-accepted' : ''}
          ${slot.AskStatus === 3 ? 'asked-badge-rejected' : ''}"
          data-tooltip="${slot.AskStatus === 3 ? '要請が承認されませんでした' : '要請'}">
          <svg version="1.1" class="" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
            viewBox="0 0 35 35" width="8px" height="8px" style="enable-background:new 0 0 35 35;" xml:space="preserve">
            <path fill="#fff" d="M33.24,3.09c-0.2-0.17-0.48-0.21-0.72-0.11L1.94,15.34C1.67,15.45,1.5,15.71,1.5,16
            c0,0.29,0.18,0.56,0.45,0.66l8.14,3.17l3.12,8.24c0.12,0.31,0.43,0.49,0.75,0.45c0.24,0.03,0.48-0.06,0.64-0.27l3.43-4.41
            l9.47,7.38c0.2,0.16,0.46,0.2,0.69,0.11c0.23-0.09,0.4-0.3,0.45-0.54l4.85-27.02C33.53,3.51,33.44,3.25,33.24,3.09z M14.6,20.43
            l-0.83,5.1l-2.26-5.98L30.65,6.14L14.6,20.43z"/>
          </svg>
        </span>
      ` : ""}
      ${ slot.Alert && canEdit ? `
        <span class="tooltip tooltip-left circle-container alert-badge" data-tooltip="${alertDescriptions[slot.Alert] || ''}">
          ${ alertIconMap[slot.Alert] || alertIconMap.default }
        </span>
      ` : ""}
    </div>
  `
}

function createSlotDOMElementChip(slot, useShiftBorders) {
  return `
    ${ slot.LinkPrev ? '<i class="icon icon-link" style="margin: 5px;"><\x2fi>' : ""}
    ${ slot.Locked && !slot.KOTSynced && !useShiftBorders ? '<i class="icon icon-check" style="margin-right: 5px;"><\x2fi>' : ""}
    ${ slot.Locked && slot.KOTSynced ? '<i class="icon icon-check" style="margin-right: 5px; color: #1E9038;"><\x2fi>' : ""}
    ${ !slot.Locked && slot.IsAuto ? '<i class="zmdi zmdi-flower" style="margin: 5px;"><\x2fi>' : "" }
    ${ slot.Repeat ? "∞" : ""}
    <p class="slot-text">${ slot.TypeTextHTML }<\x2fp>
    ${ slot.LinkNext ? '<i class="icon icon-link" style="margin-left: 3px;"><\x2fi>' : ""}
  `
}

/**
 * Returns an HTML badge SVG if this section is in the pre-computed over-limit set.
 * @param {RawSlot} section - The section data object.
 * @param {Set<number>} limitBadgeSectionIds - Set of section IDs that should show a badge.
 * @returns {string} HTML string for the badge, or empty string if no badge is needed.
 */
function createSectionLimitBadge(section, limitBadgeSectionIds) {
  if (!limitBadgeSectionIds.has(section.ID)) return '';
  return `
    <div class="section-top-right tooltip tooltip-top" data-tooltip="1日の割り当て上限に達しました">
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="section-limit-badge">
        <circle cx="5.5" cy="5.5" r="6" transform="matrix(-1 0 0 1 12 1)" fill="#F71515" stroke="white"/>
        <rect x="3.99805" y="4" width="5" height="1" fill="white"/>
        <rect x="3.99805" y="6" width="5" height="1" fill="white"/>
        <rect x="3.99805" y="8" width="5" height="1" fill="white"/>
      </svg>
    </div>`;
}

/**
 * Creates the HTML string for a single section element within a slot.
 * @param {RawSlot} section
 * @param {RawSlot} slot - The parent slot.
 * @param {Set<number>} limitBadgeSectionIds - Pre-computed set of section IDs that need a limit badge.
 * @returns {string} HTML string for the section element.
 */
function createSectionDOMElement(section, slot, limitBadgeSectionIds) {
  const isDefaulSectionColor = !section.Color || section.Color === OPLUS_COLOR_PALETTE.DEFAULT;
  const showAuto = section.IsAuto
  const slotDate = yearMonthDateString(new Date(slot.DateUnix * 1000), 'Asia/Tokyo');
  // TODO: This id is to work with legacy selectors. In the future, it might be nice to not have the "slot" prefix for sections.
  return `
    <div
        id="slot-${section.ID}"
        class="section draggable"
        data-section="true"
        data-from="${ section.FromUnix }"
        data-to="${ section.ToUnix }"
        data-slot="${ slot.ID }"
        data-id="${ section.ID }"
        data-user="${ slot.UserID }"
        data-date="${ slotDate }"
        data-background-color="${ section.Color ? `#${section.Color}` : ''}"
        data-parent-color="${ slot.Color ? `#${slot.Color}` : ''}"
        data-hide-from="${ section.HideFrom }"
        data-hide-to="${ section.HideTo }"
        data-type="${ section.Type }"
        data-is-auto="${showAuto}"
        style="
            ${slot.Color ? `box-shadow: 1px 1px 2px 2px #${slot.Color}95;` : "" }
            ${!isDefaulSectionColor ? `background-color: #${section.Color}; color: #fff;` : "" }
        "
    >
      ${createSectionLimitBadge(section, limitBadgeSectionIds)}
      <div class="section-title-outer title-outer">
      <p class="section-text ${verticalTextClass(section)}">
        <i class="zmdi zmdi-flower px-1 ${showAuto ? '' : 'd-none' }"></i>
        ${ section.Type == SLOT_TYPES.SECTION_REST && section.TypeTextHTML === "" ? "休憩" : section.TextNoTime }
        <\x2Fp>
        <i class="zmdi zmdi-delete zmdi-hc-lg dws-delete-icon d-hide mr-2"><\x2Fi>
      <\x2Fdiv>
    <\x2Fdiv>
  `
}

export function initSectionClickHandler(slot) {
  if (!getCanEdit() || !slot.Sections) {
    return
  }
  slot.Sections.forEach(sectionId => {
    $(`#slot-${sectionId}`).on("click", (e) => {
      e.stopPropagation();
      openDwsSectionModal(sectionId)
    })
  })
}

// Adds vertical orientation text to section's label if its less than 30mins
function verticalTextClass(section) {
  return isSlotLessThanThirtyMins(section) ? 'vertical-label-text' : '';
}

//setSlotMarginAndWidth uses jQuery to apply the passed margins and width
export function setSlotMarginAndWidth(selector, relativeMarginLeft, relativeSlotWidth, relativeMarginRight) {
  const el = $(selector);

  el.css('margin-left', `${relativeMarginLeft}%`);
  el.css('width', `${relativeSlotWidth}%`);
  el.css('margin-right', `${relativeMarginRight}%`);
}

//calculateSlotMarginAndWidth will calculate the margins and width of a slot or section based on the dates of its parent element (table or slot) and will account for any siblings to the left or right in the same flex row.
export function calculateSlotMarginAndWidth(slotDates, parentDates, slotSiblingLeft, slotSiblingRight) {
  const parentLengthInMs = parentDates.to - parentDates.from;
  let from = slotDates.from;
  let to = slotDates.to;
  // If a slot's From or To go beyond the parent element, then truncate it to parent's From and/or To
  if (from < parentDates.from) {
    from = parentDates.from;
  }
  if (to > parentDates.to) {
    to = parentDates.to;
  }
  // Assume we will calculate left margin in relation to parent element, table From or parent slot From
  let dateForMarginLeftComparison = parentDates.from;
  if (slotSiblingLeft) {
    // If there is an adjacent slot to the left, we calculate margin left based on its To time.
    dateForMarginLeftComparison = getSlotToDate(slotSiblingLeft);
  };
  const relativeMarginLeft = ((from - dateForMarginLeftComparison) / parentLengthInMs) * 100;
  const relativeSlotWidth = ((to - from) / parentLengthInMs) * 100;
  let relativeMarginRight = 0;
  // Only add margin right if another item will not appear to the right.
  if (!slotSiblingRight) {
    relativeMarginRight = ((parentDates.to - to) / parentLengthInMs) * 100;
  }
  return {
    relativeMarginLeft,
    relativeSlotWidth,
    relativeMarginRight,
  };
}
